import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import axios from 'axios';
import { DownloadDocumentPayload } from '@/store/fundDetails/typings';
import {
  FundDetailsImportantInfo,
  FundDetailsKeyInfoDataBridgeResponse,
  FundDetailsKeyInfoRequest,
  FundDetailsPreliminaryImportantInfoDataBridgeResponse,
  FundDetailsPremiumDiscount,
  FundDetailsPremiumDiscountRequest,
  FundDetailsPremiumLineGraph,
  FundDetailsPremiumLineGraphRequest,
} from 'model/ApiGateway';
import graphqlClientFactory from 'lib/graphql-client-factory';
import { RiskImageQueryDocument } from '@/components/graphql/RiskStatisticsImage.graphql';
import {
  CefManualKeyInfoFragment,
  CefManualKeyInfoSearchDocument,
  CefManualKeyInfoSearchQueryVariables,
} from '@/components/graphql/CefManualKeyInfoSearch.graphql';
import { rewriteLanguage } from '@/store/handleRequestLanguage';
import { apiGateway } from 'api/apiHost';

const graphQLClient = graphqlClientFactory();

const customBaseQuery = fetchBaseQuery({
  baseUrl: `${apiGateway.proxyPath}/`,
});

const requestHandler = async (args, api, extraOptions) => {
  const updatedPayload = rewriteLanguage(args);
  const result = await customBaseQuery(updatedPayload, api, extraOptions);

  if (result.error || result.data?.statusCode !== undefined) {
    const statusCode = result.data?.statusCode || result.error?.status;
    const isStatusSuccess = statusCode >= 200 && statusCode < 300;
    if (!isStatusSuccess) {
      return {
        error: {
          statusCode,
        },
      };
    }
  }

  return result;
};

export const fundDetailsApi = createApi({
  reducerPath: 'fundDetailsApi',
  baseQuery: requestHandler,
  endpoints: (build) => ({
    fetchFundDetailsDocuments: build.query({
      query: (body) => ({
        url: 'library/fundHero',
        method: 'POST',
        body: body,
      }),
    }),
    fetchFundFactsTabData: build.query({
      query: (body) => ({
        url: 'funds/text/fundObjectives',
        method: 'POST',
        body: body,
      }),
    }),
    fetchKeyInformationTabData: build.query<
      FundDetailsKeyInfoDataBridgeResponse,
      FundDetailsKeyInfoRequest
    >({
      query: (body) => ({
        url: 'funds/fundDetailsKeyInformation',
        method: 'POST',
        body: body,
      }),
    }),
    fetchDisclaimerData: build.query({
      query: (body) => ({
        url: 'funds/text/keyInfoDisclaimer',
        method: 'POST',
        body: body,
      }),
    }),
    fetchFundDetailsImportantInformation: build.query({
      query: (body) => ({
        url: 'funds/text/importantInfo',
        method: 'POST',
        body: body,
      }),
    }),
    fetchPreliminaryImportantInformation: build.query<
      FundDetailsPreliminaryImportantInfoDataBridgeResponse,
      FundDetailsImportantInfo
    >({
      query: (body) => ({
        url: 'funds/text/preliminaryimportantInfo',
        method: 'POST',
        body: body,
      }),
    }),
    fetchFundRiskStatisticsTabData: build.query({
      queryFn: async (_arg, _queryApi, _extraOptions, fetchWithBQ) => {
        const { data } = await fetchWithBQ({
          url: 'funds/funddetailsriskstatistics',
          method: 'POST',
          body: _arg,
        });

        const result = await graphQLClient.request<any>(RiskImageQueryDocument as any, {
          fundSettingsId: _arg.fundSettingsId,
          language: _arg.language,
        });

        return {
          data: {
            ...data,
            riskImageData: result.item.children.results[0]?.children.results ?? [],
          },
        };
      },
    }),
    fetchKeyRiskTabData: build.query({
      query: (body) => ({
        url: 'funds/text/keyRisk',
        method: 'POST',
        body: body,
      }),
    }),
    fetchDerivativeTabData: build.query({
      query: (body) => ({
        url: 'funds/text/derivativeUsage',
        method: 'POST',
        body: body,
      }),
    }),
    fetchPortfolioSecuritiesTabData: build.query({
      query: (body) => ({
        url: 'funds/text/portfolioSecurities',
        method: 'POST',
        body: body,
      }),
    }),
    fetchTradingInformationTabData: build.query({
      query: (body) => ({
        url: 'funds/funddetailstradinginformation',
        method: 'POST',
        body: body,
      }),
      transformResponse: (response: any) => {
        const content = response?.content;
        if (!Array.isArray(content) || content.length === 0) return [];

        const columnsOrder = [
          'name',
          'exchangeCode',
          'currency',
          'cusip',
          'ric',
          'sedol',
          'bloombergCode',
          'bloombergTicker',
          'reutersCode',
          'tradingHours',
        ];

        return content.map((row) => {
          return columnsOrder
            .filter((column) => column in row)
            .reduce((ordered, column) => {
              ordered[column] = row[column];
              return ordered;
            }, {} as Record<string, any>);
        });
      },
    }),
    fetchAuthorisedCountriesTabData: build.query({
      query: (body) => ({
        url: 'funds/funddetailsauthorizedcountries',
        method: 'POST',
        body: body,
      }),
    }),
    fetchCodesTabData: build.query({
      query: (body) => ({
        url: 'funds/fundDetailsCodes',
        method: 'POST',
        body: body,
      }),
    }),
    fetchRiskAnalysisTabData: build.query({
      query: (body) => ({
        url: 'funds/breakdown/risk',
        method: 'POST',
        body: body,
      }),
      transformResponse: (response: any) => {
        if (response.statusCode && response.statusCode !== 200) {
          return {
            statusCode: response.statusCode,
          };
        }
        response.content.results.forEach((item) => {
          item.columns.sort((a, b) => a.columnDisplayOrder - b.columnDisplayOrder);
        });
        return response;
      },
    }),
    fetchHoldingsTabData: build.query({
      query: (body) => ({
        url: body.topHoldings ? 'funds/breakdown/topTenHoldings' : 'funds/breakdown/dailyHoldings',
        method: 'POST',
        body: body,
      }),
    }),
    fetchHoldingsTabManualDisclaimerData: build.query({
      query: (body) => ({
        url: 'funds/text/holdingsDisclaimer',
        method: 'POST',
        body: body,
      }),
    }),
    fetchLiteratureTabData: build.query({
      query: (body) => ({
        url: 'library/fund',
        method: 'POST',
        body: body,
      }),
    }),
    fetchFundDetailsBreakdown: build.query({
      query: (body) => ({
        url: `funds/breakdown/${body.endpoint}`,
        method: 'POST',
        body: body,
      }),
      transformResponse: (response) => {
        if (response?.content) {
          return response.content;
        } else {
          return response;
        }
      },
    }),
    fetchFundDetailsPerformance: build.query({
      query: (body) => ({
        url: `funds/${body.endpoint}`,
        method: 'POST',
        body: body,
      }),
    }),
    fetchManagementTabData: build.query({
      query: (body) => ({
        url: 'funds/text/management',
        method: 'POST',
        body: body,
      }),
    }),
    fetchCumulativeChartData: build.query({
      query: (body) => ({
        url: 'funds/performance/cumulativeChart',
        method: 'POST',
        body: body,
      }),
      transformResponse: (resposnse) => {
        resposnse.content.categories.sort((a, b) => {
          if (a.label > b.label) {
            return 1;
          } else {
            return -1;
          }
        });

        return resposnse.content;
      },
    }),
    fetchPremiumLineChartData: build.query<
      FundDetailsPremiumLineGraph,
      FundDetailsPremiumLineGraphRequest
    >({
      query: (body) => ({
        url: 'funds/fundDetailsPremiumLineGraph',
        method: 'POST',
        body: body,
      }),
    }),
    fetchPremiumBarChartData: build.query<
      FundDetailsPremiumDiscount,
      FundDetailsPremiumDiscountRequest
    >({
      query: (body) => ({
        url: 'funds/fundDetailsPremiumDiscount',
        method: 'POST',
        body: body,
      }),
    }),
    fetchCalendarTabData: build.query({
      query: (body) => ({
        url: 'funds/performance/calendar',
        method: 'POST',
        body: body,
      }),
    }),
    fetchKeyInfoCefFields: build.query<
      CefManualKeyInfoFragment,
      CefManualKeyInfoSearchQueryVariables
    >({
      queryFn: async (_arg) => {
        const result = await graphQLClient.request<any>(CefManualKeyInfoSearchDocument as any, {
          fundId: _arg.fundId,
          language: _arg.language,
          pageSize: 1,
          pageHash: '',
        });

        return {
          data: result.search.results[0] as CefManualKeyInfoFragment,
        };
      },
    }),
  }),
});

export const DownloadFile = (payload: DownloadDocumentPayload) => {
  return axios.post(`${apiGateway.proxyPath}/funds/breakdown/dailyHoldingsSheet`, payload, {
    responseType: 'blob',
  });
};

export const {
  useFetchFundDetailsDocumentsQuery,
  useFetchFundFactsTabDataQuery,
  useFetchKeyInformationTabDataQuery,
  useFetchDisclaimerDataQuery,
  useFetchFundDetailsImportantInformationQuery,
  useFetchFundRiskStatisticsTabDataQuery,
  useFetchKeyRiskTabDataQuery,
  useFetchDerivativeTabDataQuery,
  useFetchPortfolioSecuritiesTabDataQuery,
  useFetchTradingInformationTabDataQuery,
  useFetchAuthorisedCountriesTabDataQuery,
  useFetchCodesTabDataQuery,
  useFetchRiskAnalysisTabDataQuery,
  useLazyFetchHoldingsTabDataQuery,
  useLazyFetchHoldingsTabManualDisclaimerDataQuery,
  useFetchLiteratureTabDataQuery,
  useFetchFundDetailsBreakdownQuery,
  useFetchFundDetailsPerformanceQuery,
  useFetchCumulativeChartDataQuery,
  useLazyFetchCumulativeChartDataQuery,
  useFetchManagementTabDataQuery,
  useFetchPremiumLineChartDataQuery,
  useFetchPremiumBarChartDataQuery,
  useFetchCalendarTabDataQuery,
  useLazyFetchPremiumBarChartDataQuery,
  useFetchPreliminaryImportantInformationQuery,
  useLazyFetchKeyInfoCefFieldsQuery,
} = fundDetailsApi;
